import { router } from '@inertiajs/react';
import { Image as ImageIcon, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import type { AdminMentorsIndex } from '@/types';

interface MentorPictureUploadProps {
  mentorId: string;
  eventSlug: string;
  currentPictureUrl: string | null;
  mentorName: string;
  onUploaded?: (pictureUrl: string) => void;
}

const MAX_SIZE_BYTES = 2 * 1024 * 1024;

export function MentorPictureUpload({
  mentorId,
  eventSlug,
  currentPictureUrl,
  mentorName,
  onUploaded,
}: MentorPictureUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPending, setIsPending] = useState(false);
  // The data URL shown while the upload is in flight. Once the mutation lands, the
  // stored URL arrives on the refreshed `mentors` prop, so the local copy is dropped.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const shownUrl = previewUrl ?? currentPictureUrl;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ type: 'error', description: 'Please select a valid image file' });
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      toast({
        type: 'error',
        description: 'Image size must be less than 2MB',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64String = e.target?.result as string;
      setPreviewUrl(base64String);
      setIsPending(true);

      router.patch(
        `/admin/${eventSlug}/mentors/${mentorId}/picture`,
        { image: base64String },
        {
          preserveScroll: true,
          preserveState: true,
          onSuccess: (page) => {
            const { mentors } = page.props as unknown as AdminMentorsIndex;
            const stored = mentors.find((mentor) => mentor.id === mentorId);
            if (stored?.pictureUrl) onUploaded?.(stored.pictureUrl);
            toast({ type: 'success', description: 'Mentor picture updated' });
          },
          onError: (errors) =>
            toast({
              type: 'error',
              description:
                (errors as unknown as Record<string, string>).picture ??
                'Failed to update picture',
            }),
          onFinish: () => {
            setPreviewUrl(null);
            setIsPending(false);
          },
        },
      );
    };

    reader.onerror = () => {
      toast({ type: 'error', description: 'Failed to read image file' });
    };

    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4">
        {shownUrl ? (
          <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg border-2 border-border bg-muted">
            <img
              src={shownUrl}
              alt={mentorName ? `Picture of ${mentorName}` : 'Mentor picture'}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        ) : (
          <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-lg border-2 border-border border-dashed bg-muted">
            <ImageIcon className="h-8 w-8 text-muted-foreground" />
          </div>
        )}

        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            disabled={isPending}
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isPending}
            variant="outline"
            size="sm"
          >
            <Upload className="mr-2 h-4 w-4" />
            {isPending ? 'Uploading…' : 'Upload picture'}
          </Button>
          <p className="text-muted-foreground text-xs">
            Square crop, max 2MB (PNG, JPG, WebP)
          </p>
        </div>
      </div>
    </div>
  );
}
